import React, { useRef, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import { MdCheckCircle } from "react-icons/md";
import { MdCancel } from "react-icons/md";
import { FiEye, FiEyeOff } from "react-icons/fi";

import styles from "./Input.module.scss";

export default function Input({
  id,
  type = "",
  placeholder = "",
  value,
  label = "",
  helpText,
  onChange,
  disabled = false,
  onFocused = false,
  min,
  max,
  maxLength,
  required = false,
  valid,
  message = "",
  className = "",
  useBorder = true,
  iconBefore,
  iconAfter,
  clickableIcon = false,
  iconClassName = "",
  prefix,
  suffix,
  style = {},
  width = "",
  fullWidth = false,
  step,
  onBlur,
  ...rest
}) {
  const inputRef = useRef(null);
  const generatedId = useRef(`input-${uuidv4()}`).current;
  const inputId = id || rest.id || generatedId;

  const [input, setInput] = useState("");
  const [status, setStatus] = useState({ valid: undefined });
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState(type);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      setInput(value);
    }
  }, [value]);

  useEffect(() => {
    if (onFocused) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 0);
    }
  }, [onFocused]);

  useEffect(() => {
    setStatus({
      valid: toBoolean(valid),
    });
  }, [valid]);

  useEffect(() => {
    // Update input type when type prop changes or showPassword state changes
    if (type === "password") {
      setInputType(showPassword ? "text" : "password");
    } else {
      setInputType(type);
    }
  }, [type, showPassword]);

  const toBoolean = (value) => {
    if (value === undefined) return undefined;
    if (typeof value === "string") return value?.toLowerCase() === "true";
    return value;
  };

  const onChangeInput = (event) => {
    const rawVal = event?.target?.value ?? "";
    if (typeof onChange === "function") {
      let valObj;
      try {
        valObj = Object.assign(new String(rawVal), {
          target: event?.target || { value: rawVal },
        });
      } catch (e) {
        valObj = rawVal;
      }
      onChange(valObj, event);
    }
    setInput(rawVal);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const applyClasses = () => {
    const classes = [];

    if (status?.valid !== undefined) {
      if (status?.valid) classes.push("input-container__input--valid");
      else classes.push("input-container__input--invalid");
    }

    if (iconBefore || prefix)
      classes.push("input-container__input--icon-before");
    if (iconAfter || suffix || type === "password")
      classes.push("input-container__input--icon-after");

    return classes;
  };

  const statusClass = () => {
    if (status?.valid !== undefined) {
      if (status?.valid) return "input-container__message--valid";
      else return "input-container__message--invalid";
    }

    return;
  };

  const onFocus = () => {
    if (valid === undefined) {
      setStatus({
        valid: undefined,
      });
    }
  };

  // Render password toggle icon if input type is password
  const renderPasswordToggle = () => {
    if (type === "password") {
      return (
        <span
          className={`${styles["input-icon"]} ${styles["input-icon--after"]} ${styles["password-toggle"]}`}
          onClick={togglePasswordVisibility}
          style={{ cursor: "pointer", color: "#6c757d" }}
        >
          {showPassword ? <FiEyeOff /> : <FiEye />}
        </span>
      );
    }
    return null;
  };

  return (
    <div
      className={`${styles["input-container"]} ${
        !useBorder && styles["input-container--no-border"]
      } ${width || (fullWidth ? "w-100" : "")}`}
    >
      {label ? (
        <label htmlFor={inputId} className={styles["input-container__label"]}>
          <span>{label}</span>
          {required && (
            <span className={styles["input-container__label--required"]}>*</span>
          )}
        </label>
      ) : null}

      <div className="u-relative d-flex align-items-center">
        <input
          className={`${styles["input-container__input"]} ${
            className && className
          } ${applyClasses()?.length ? styles[applyClasses().join(" ")] : ""} `}
          type={inputType}
          ref={inputRef}
          value={input}
          onChange={(event) => onChangeInput(event)}
          disabled={disabled || false}
          placeholder={placeholder}
          min={min}
          max={max}
          maxLength={maxLength}
          step={step}
          onFocus={(event) => onFocus(event)}
          onBlur={onBlur}
          style={style}
          {...rest}
          id={inputId}
          aria-invalid={status?.valid === false ? true : (rest["aria-invalid"] || undefined)}
          aria-describedby={
            status?.valid === false && message
              ? `${inputId}-error`
              : helpText
              ? `${inputId}-help`
              : rest["aria-describedby"]
          }
        />

        {prefix && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--before"]
            } ${iconClassName !== undefined && iconClassName}`}
          >
            {prefix}
          </span>
        )}

        {iconBefore && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--before"]
            } ${iconClassName !== undefined && iconClassName}`}
            onClick={() => clickableIcon && setInput("")}
          >
            {iconBefore}
          </span>
        )}

        {suffix && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--after"]
            } ${iconClassName !== undefined && iconClassName} ps-5 pe-5`}
          >
            {suffix}
          </span>
        )}

        {iconAfter && (
          <span
            className={`${styles["input-icon"]} ${
              styles["input-icon--after"]
            } ${iconClassName !== undefined && iconClassName}`}
            onClick={() => clickableIcon && setInput("")}
          >
            {iconAfter}
          </span>
        )}

        {/* Render password toggle if no suffix is provided */}
        {type === "password" && !suffix && renderPasswordToggle()}

        {status?.valid !== undefined && (
          <span className={styles["input-container__input-icons"]}>
            {status?.valid ? (
              <MdCheckCircle
                className={styles["input-container__input-icons--valid"]}
              />
            ) : (
              <MdCancel
                className={styles["input-container__input-icons--invalid"]}
              />
            )}
          </span>
        )}

        {status?.valid !== undefined && message && (
          <span
            id={`${inputId}-error`}
            role="alert"
            className={`${styles["input-container__message"]} ${
              styles[statusClass()]
            }`}
          >
            {message || "Required field"}
          </span>
        )}
      </div>

      {helpText && (
        <small
          id={`${inputId}-help`}
          className="text-muted d-block mt-1"
          style={{ fontSize: "12px", lineHeight: "1.3" }}
        >
          {helpText}
        </small>
      )}
    </div>
  );
}
